import { Component, type ErrorInfo, type ReactNode } from 'react'
import * as Sentry from '@sentry/react'
import { Button } from '@/components/ui/button'
import { hardReset } from '@/shared/lib/swHealing'

// ErrorBoundary renders outside I18nProvider, so we cannot use Trans/t macros.
// Instead, use navigator.language for basic FR/DE fallback.
const ERROR_MESSAGES: Record<
  string,
  { title: string; desc: string; reload: string; reset: string }
> = {
  en: {
    title: 'Something went wrong',
    desc: 'An unexpected error occurred. Please reload the page.',
    reload: 'Reload',
    reset: 'Reset and reload',
  },
  fr: {
    title: 'Une erreur est survenue',
    desc: "Une erreur inattendue s'est produite. Veuillez recharger la page.",
    reload: 'Recharger',
    reset: 'Réinitialiser et recharger',
  },
  de: {
    title: 'Etwas ist schiefgelaufen',
    desc: 'Ein unerwarteter Fehler ist aufgetreten. Bitte laden Sie die Seite neu.',
    reload: 'Neu laden',
    reset: 'Zurücksetzen und neu laden',
  },
}

function getMessages() {
  const lang = navigator.language?.slice(0, 2) || 'en'
  return ERROR_MESSAGES[lang] || ERROR_MESSAGES.en
}

interface Props {
  children: ReactNode
}

interface State {
  error: Error | null
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error) {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Without this the daily home-page crash is completely invisible — nothing
    // reaches Sentry because the boundary swallows the error synchronously.
    console.error('[ErrorBoundary] render error', error, info.componentStack)
    Sentry.captureException(error, {
      contexts: { react: { componentStack: info.componentStack ?? '' } },
    })
  }

  render() {
    if (this.state.error) {
      const msg = getMessages()
      return (
        <div className="flex min-h-svh flex-col items-center justify-center px-6 text-center">
          <h2 className="text-destructive text-lg font-semibold">
            {msg.title}
          </h2>
          <p className="text-muted-foreground mt-2 text-sm">{msg.desc}</p>
          <div className="mt-4 flex flex-col items-center gap-2">
            <Button
              onClick={() => {
                this.setState({ error: null })
                window.location.reload()
              }}
            >
              {msg.reload}
            </Button>
            {/* Escape hatch for users stuck on an old Service Worker that keeps
             * serving a broken bundle no matter how often they reload — wipes
             * every cache and unregisters every SW before reloading. */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                hardReset()
              }}
            >
              {msg.reset}
            </Button>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
